import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { proFormaInvoiceService } from '../../services/proforma-invoice.service';
import { geocodingService, LocationSuggestion } from '../../services/geocoding.service';
import { LocationAutocomplete } from '../../components/ui/LocationAutocomplete';

const schema = z.object({
  origin: z.string().min(1, 'Origin is required'),
  destination: z.string().min(1, 'Destination is required'),
  distanceKm: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive('Distance must be positive')
  ),
  ratePerKm: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive('Rate per km must be positive')
  ),
  additionalCharges: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? 0 : Number(value)),
    z.number().min(0, 'Additional charges cannot be negative')
  ),
  additionalChargesDescription: z.string().optional(),
  taxAmount: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? 0 : Number(value)),
    z.number().min(0, 'Tax amount cannot be negative')
  ),
  clientName: z.string().min(1, 'Client name is required'),
  clientContact: z.string().optional(),
  clientReference: z.string().optional(),
  validUntil: z.string().min(1, 'Valid until date is required'),
  notes: z.string().optional(),
});

type FormInput = z.infer<typeof schema>;

interface GenerateProFormaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GenerateProFormaModal: React.FC<GenerateProFormaModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [originLocation, setOriginLocation] = useState<LocationSuggestion | null>(null);
  const [destLocation, setDestLocation] = useState<LocationSuggestion | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      additionalCharges: 0,
      taxAmount: 0,
    },
  });

  const mutation = useMutation({
    mutationFn: proFormaInvoiceService.create,
    onSuccess: () => {
      toast.success('Pro forma invoice generated successfully');
      queryClient.invalidateQueries({ queryKey: ['proFormaInvoices'] });
      reset();
      setOriginLocation(null);
      setDestLocation(null);
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to generate pro forma invoice');
    },
  });

  const handleOriginSelect = (location: LocationSuggestion) => {
    setOriginLocation(location);
  };

  const handleDestSelect = (location: LocationSuggestion) => {
    setDestLocation(location);
  };

  const calculateDistance = async () => {
    if (!originLocation || !destLocation) {
      toast.error('Please select both origin and destination');
      return;
    }

    setIsCalculating(true);
    try {
      const result = await geocodingService.calculateDistance(
        originLocation.lat,
        originLocation.lon,
        destLocation.lat,
        destLocation.lon
      );

      if (result) {
        setValue('distanceKm', result.distanceKm, { shouldValidate: true });
        toast.success(`Distance calculated: ${result.distanceKm} km`);
      } else {
        toast.error('Could not calculate distance. Please enter manually.');
      }
    } catch (error) {
      toast.error('Failed to calculate distance');
    } finally {
      setIsCalculating(false);
    }
  };

  const onSubmit = (data: FormInput) => {
    const parsed = schema.parse(data);
    mutation.mutate(parsed);
  };

  const handleClose = () => {
    reset();
    setOriginLocation(null);
    setDestLocation(null);
    onClose();
  };

  const origin = watch('origin');
  const destination = watch('destination');
  const distanceKm = watch('distanceKm');
  const ratePerKm = watch('ratePerKm');
  const additionalCharges = watch('additionalCharges');
  const taxAmount = watch('taxAmount');

  const distanceCost = distanceKm && ratePerKm ? Number(distanceKm) * Number(ratePerKm) : 0;
  const subtotal = distanceCost + (Number(additionalCharges) || 0);
  const totalAmount = subtotal + (Number(taxAmount) || 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Generate Pro Forma Invoice"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={mutation.isPending}>
            Generate
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <LocationAutocomplete
              label="Origin"
              placeholder="Search origin city..."
              value={origin || ''}
              onChange={(value) => setValue('origin', value)}
              onLocationSelect={handleOriginSelect}
              error={errors.origin?.message}
              required
            />
            <input type="hidden" {...register('origin')} />
          </div>

          <div>
            <LocationAutocomplete
              label="Destination"
              placeholder="Search destination city..."
              value={destination || ''}
              onChange={(value) => setValue('destination', value)}
              onLocationSelect={handleDestSelect}
              error={errors.destination?.message}
              required
            />
            <input type="hidden" {...register('destination')} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Distance (km)"
              type="number"
              placeholder="e.g., 486"
              error={errors.distanceKm?.message}
              required
              {...register('distanceKm')}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={calculateDistance}
              disabled={!originLocation || !destLocation || isCalculating}
              className="mt-2"
            >
              {isCalculating ? 'Calculating...' : 'Calculate Distance'}
            </Button>
          </div>

          <Input
            label="Rate per km (TZS)"
            type="number"
            placeholder="e.g., 2000"
            error={errors.ratePerKm?.message}
            required
            {...register('ratePerKm')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Additional Charges (TZS)"
            type="number"
            placeholder="e.g., 50000"
            error={errors.additionalCharges?.message}
            {...register('additionalCharges')}
          />

          <Input
            label="Additional Charges Description"
            placeholder="e.g., Toll fees and parking"
            error={errors.additionalChargesDescription?.message}
            {...register('additionalChargesDescription')}
          />
        </div>

        <Input
          label="Tax Amount (TZS)"
          type="number"
          placeholder="e.g., 0"
          error={errors.taxAmount?.message}
          {...register('taxAmount')}
        />

        <div className="border-t pt-4">
          <h4 className="font-semibold mb-3">Client Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Client Name"
              placeholder="e.g., XYZ Corporation"
              error={errors.clientName?.message}
              required
              {...register('clientName')}
            />

            <Input
              label="Client Contact"
              placeholder="e.g., +255712345678"
              error={errors.clientContact?.message}
              {...register('clientContact')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Input
              label="Client Reference"
              placeholder="e.g., RFQ-2024-005"
              error={errors.clientReference?.message}
              {...register('clientReference')}
            />

            <Input
              label="Valid Until"
              type="date"
              error={errors.validUntil?.message}
              required
              {...register('validUntil')}
            />
          </div>
        </div>

        <Input
          label="Notes"
          placeholder="e.g., Quote for upcoming transport service"
          error={errors.notes?.message}
          {...register('notes')}
        />

        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <div className="flex justify-between text-sm">
            <span>Distance Cost:</span>
            <span className="font-medium">
              {new Intl.NumberFormat('en-TZ', {
                style: 'currency',
                currency: 'TZS',
                minimumFractionDigits: 0,
              }).format(Number(distanceCost))}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Additional Charges:</span>
            <span className="font-medium">
              {new Intl.NumberFormat('en-TZ', {
                style: 'currency',
                currency: 'TZS',
                minimumFractionDigits: 0,
              }).format(Number(additionalCharges) || 0)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Subtotal:</span>
            <span className="font-medium">
              {new Intl.NumberFormat('en-TZ', {
                style: 'currency',
                currency: 'TZS',
                minimumFractionDigits: 0,
              }).format(Number(subtotal))}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Tax:</span>
            <span className="font-medium">
              {new Intl.NumberFormat('en-TZ', {
                style: 'currency',
                currency: 'TZS',
                minimumFractionDigits: 0,
              }).format(Number(taxAmount) || 0)}
            </span>
          </div>
          <div className="flex justify-between text-lg font-bold border-t pt-2">
            <span>Total Amount:</span>
            <span>
              {new Intl.NumberFormat('en-TZ', {
                style: 'currency',
                currency: 'TZS',
                minimumFractionDigits: 0,
              }).format(Number(totalAmount))}
            </span>
          </div>
        </div>
      </form>
    </Modal>
  );
};
